import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Package, DollarSign, Users, ChevronLeft, ChevronRight, Loader2,
  Settings, Save, Building, Landmark, Download, Truck, RotateCcw, FileSpreadsheet
} from "lucide-react";

const MONTH_NAMES = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const AdminBilling = ({ user, onLogout }) => {
  const [mainTab, setMainTab] = useState("data");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Settings state
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    bill_from: { name: "", address: "", gst_number: "", state_code: "" },
    bank_details: { account_number: "", bank_name: "", ifsc_code: "", city: "", cheque_payable_to: "" },
  });

  // Invoice download state (invoices are generated by the external billing system
  // and written back via POST /admin-billing/external-invoice; this app no longer
  // generates them — it only lists + downloads them).
  const [generatingInvoice, setGeneratingInvoice] = useState(null);

  // B2C billing state
  const [b2cLoading, setB2cLoading] = useState(true);
  const [b2cSummary, setB2cSummary] = useState(null);
  const [b2cData, setB2cData] = useState([]);
  const [b2cPage, setB2cPage] = useState(1);
  const [b2cTotalPages, setB2cTotalPages] = useState(1);
  const [b2cGenerating, setB2cGenerating] = useState(null);

  // Per-row "raw AWB data" CSV export (both B2B and B2C tables).
  const [downloadingRaw, setDownloadingRaw] = useState(null);

  const fetchB2C = useCallback(async () => {
    try {
      setB2cLoading(true);
      const [summaryRes, dataRes] = await Promise.all([
        axios.get(`${API}/admin-billing/b2c/summary`),
        axios.get(`${API}/admin-billing/b2c/monthly-seller?page=${b2cPage}&size=20`),
      ]);
      setB2cSummary(summaryRes.data);
      setB2cData(dataRes.data.data || []);
      setB2cTotalPages(dataRes.data.total_pages || 1);
    } catch {
      toast.error("Failed to load B2C billing data");
    } finally {
      setB2cLoading(false);
    }
  }, [b2cPage]);

  const downloadB2CInvoice = async (row) => {
    const dlKey = `dl-${row.seller_id}-${row.year}-${row.month}`;
    setB2cGenerating(dlKey);
    try {
      const response = await axios.get(
        `${API}/admin-billing/b2c/download-invoice?seller_id=${row.seller_id}&year=${row.year}&month=${row.month}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const safeInv = (row.invoice_number || "b2c_invoice").replace(/\//g, "_");
      link.setAttribute("download", `${safeInv}_${row.seller_id}_${MONTH_NAMES[row.month]}_${row.year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("B2C invoice downloaded");
    } catch {
      toast.error("Failed to download B2C invoice");
    } finally {
      setB2cGenerating(null);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [summaryRes, dataRes] = await Promise.all([
        axios.get(`${API}/admin-billing/summary`),
        axios.get(`${API}/admin-billing/monthly-seller?page=${page}&size=20`),
      ]);
      setSummary(summaryRes.data);
      setData(dataRes.data.data || []);
      setTotalPages(dataRes.data.total_pages || 1);
    } catch {
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  }, [page]);

  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res = await axios.get(`${API}/admin-billing/settings`);
      setSettings(res.data);
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchSettings();
  }, []);

  useEffect(() => {
    if (mainTab === "data") fetchData();
    else if (mainTab === "settings") fetchSettings();
    else if (mainTab === "b2c") fetchB2C();
  }, [mainTab, page, b2cPage]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin-billing/settings`, {
        bill_from: settings.bill_from,
        bank_details: settings.bank_details,
      });
      setSettings(res.data);
      toast.success("Billing settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateBillFrom = (field, value) => {
    setSettings(prev => ({ ...prev, bill_from: { ...prev.bill_from, [field]: value } }));
  };
  const updateBankDetails = (field, value) => {
    setSettings(prev => ({ ...prev, bank_details: { ...prev.bank_details, [field]: value } }));
  };

  const downloadExistingInvoice = async (row) => {
    const invoiceKey = `dl-${row.seller_id}-${row.year}-${row.month}`;
    setGeneratingInvoice(invoiceKey);
    try {
      const response = await axios.get(
        `${API}/admin-billing/download-invoice?seller_id=${row.seller_id}&year=${row.year}&month=${row.month}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const safeInv = (row.invoice_number || "invoice").replace(/\//g, "_");
      link.setAttribute("download", `invoice_${safeInv}_${row.seller_id}_${MONTH_NAMES[row.month]}_${row.year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Invoice downloaded");
    } catch (err) {
      toast.error("Failed to download invoice");
    } finally {
      setGeneratingInvoice(null);
    }
  };

  // Per-shipment raw export (AWB/LRN + freight breakdown) for one seller/month.
  // scope "b2b" or "b2c" picks the matching endpoint.
  const downloadRawShipments = async (row, scope) => {
    const rawKey = `raw-${scope}-${row.seller_id}-${row.year}-${row.month}`;
    setDownloadingRaw(rawKey);
    try {
      const path = scope === "b2c" ? "b2c/raw-shipments.csv" : "raw-shipments.csv";
      const response = await axios.get(
        `${API}/admin-billing/${path}?seller_id=${row.seller_id}&year=${row.year}&month=${row.month}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const mm = String(row.month).padStart(2, "0");
      link.setAttribute("download", `${scope}_raw_${row.seller_id}_${row.year}_${mm}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Raw AWB data downloaded");
    } catch {
      toast.error("Failed to download raw AWB data");
    } finally {
      setDownloadingRaw(null);
    }
  };

  const fmt = (v) => {
    if (v == null) return "0";
    return Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
    <div className="space-y-6" data-testid="admin-billing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Billing</h1>
          <p className="text-sm text-slate-500 mt-1">Month-wise seller billing summary</p>
        </div>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="data" className="gap-2" data-testid="billing-data-tab">
            <DollarSign className="w-4 h-4" /> B2B Billing
          </TabsTrigger>
          <TabsTrigger value="b2c" className="gap-2" data-testid="billing-b2c-tab">
            <Truck className="w-4 h-4" /> B2C Billing
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2" data-testid="billing-settings-tab">
            <Settings className="w-4 h-4" /> Billing Settings
          </TabsTrigger>
        </TabsList>

        {/* ======= DATA TAB ======= */}
        <TabsContent value="data">
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">Total Delivered</p>
                      <p className="text-3xl font-bold text-blue-800">{summary.total_delivered}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                      <Package className="w-6 h-6 text-blue-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-50 to-amber-100">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-600">Total Returned</p>
                      <p className="text-3xl font-bold text-amber-800">{summary.total_returned}</p>
                    </div>
                    <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center">
                      <Package className="w-6 h-6 text-amber-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Total Charges</p>
                      <p className="text-2xl font-bold text-green-800">₹{fmt(summary.total_charges)}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600">Sellers</p>
                      <p className="text-3xl font-bold text-purple-800">{summary.seller_count}</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Monthly Seller Table */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Month Wise Seller Billing</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-billing-table">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Month</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Seller</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Delivered</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Returned</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Total</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Fwd Pre-Tax</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Fwd GST (18%)</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">RTO Pre-Tax</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">RTO GST (18%)</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Taxable Value</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Tax Amount</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Total Charges</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Invoice</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Raw AWB</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.length === 0 ? (
                          <tr><td colSpan={14} className="text-center p-8 text-muted-foreground">No billing data available</td></tr>
                        ) : data.map((row, i) => {
                          const dlKey = `dl-${row.seller_id}-${row.year}-${row.month}`;
                          const rawKey = `raw-b2b-${row.seller_id}-${row.year}-${row.month}`;
                          const taxable = (Number(row.fwd_pre_tax) || 0) + (Number(row.rto_pre_tax) || 0);
                          const taxAmount = (Number(row.fwd_gst) || 0) + (Number(row.rto_gst) || 0);
                          return (
                            <tr key={i} className="border-b hover:bg-muted/30">
                              <td className="px-4 py-3 align-middle font-medium">{MONTH_NAMES[row.month]} {row.year}</td>
                              <td className="px-4 py-3 align-middle">{row.seller_name}</td>
                              <td className="px-4 py-3 align-middle text-right">{row.delivered}</td>
                              <td className="px-4 py-3 align-middle text-right">{row.returned}</td>
                              <td className="px-4 py-3 align-middle text-right font-medium">{row.total_orders}</td>
                              <td className="px-4 py-3 align-middle text-right">₹{fmt(row.fwd_pre_tax)}</td>
                              <td className="px-4 py-3 align-middle text-right text-slate-500">₹{fmt(row.fwd_gst)}</td>
                              <td className="px-4 py-3 align-middle text-right">₹{fmt(row.rto_pre_tax)}</td>
                              <td className="px-4 py-3 align-middle text-right text-slate-500">₹{fmt(row.rto_gst)}</td>
                              <td className="px-4 py-3 align-middle text-right">₹{fmt(taxable)}</td>
                              <td className="px-4 py-3 align-middle text-right text-slate-500">₹{fmt(taxAmount)}</td>
                              <td className="px-4 py-3 align-middle text-right font-medium text-green-700">₹{fmt(row.total_charges)}</td>
                              <td className="px-4 py-3 align-middle text-center">
                                {row.invoice_number ? (
                                  <div className="flex flex-col items-center gap-1">
                                    <span className="text-xs font-medium text-green-700">{row.invoice_number}</span>
                                    <Button
                                      variant="ghost" size="sm"
                                      onClick={() => downloadExistingInvoice(row)}
                                      disabled={generatingInvoice === dlKey}
                                      className="text-green-600 hover:bg-green-50 h-6 px-2"
                                      data-testid={`download-invoice-${row.seller_id}-${row.year}-${row.month}`}
                                    >
                                      {generatingInvoice === dlKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400" title="Invoices are generated by the external billing system and written back via the API; they appear here once received.">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 align-middle text-center">
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => downloadRawShipments(row, "b2b")}
                                  disabled={downloadingRaw === rawKey || !row.total_orders}
                                  title="Download per-shipment raw data (AWB/LRN + freight breakdown) for this seller & month"
                                  className="text-slate-600 hover:bg-slate-100 h-6 px-2"
                                  data-testid={`download-raw-b2b-${row.seller_id}-${row.year}-${row.month}`}
                                >
                                  {downloadingRaw === rawKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t">
                      <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ======= B2C BILLING TAB ======= */}
        <TabsContent value="b2c">
          {b2cSummary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-50 to-teal-100">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-teal-600">Forward Shipments</p>
                      <p className="text-3xl font-bold text-teal-800">{b2cSummary.forward_orders}</p>
                    </div>
                    <div className="w-12 h-12 bg-teal-200 rounded-full flex items-center justify-center"><Truck className="w-6 h-6 text-teal-700" /></div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-50 to-amber-100">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-600">Return Shipments</p>
                      <p className="text-3xl font-bold text-amber-800">{b2cSummary.return_orders}</p>
                    </div>
                    <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center"><RotateCcw className="w-6 h-6 text-amber-700" /></div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Total Charges</p>
                      <p className="text-2xl font-bold text-green-800">₹{fmt(b2cSummary.total_charges)}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center"><DollarSign className="w-6 h-6 text-green-700" /></div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600">Sellers</p>
                      <p className="text-3xl font-bold text-purple-800">{b2cSummary.seller_count}</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center"><Users className="w-6 h-6 text-purple-700" /></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Month Wise B2C Seller Billing</CardTitle>
              <CardDescription>B2C last-mile shipping charged to the seller's wallet (forward + returns), by ship/charge date. COD payouts are excluded. Invoice numbers are auto-generated (EDGBI-…).</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {b2cLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-b2c-billing-table">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Month</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Seller</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Forward</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Returns</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Total Orders</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Taxable Value</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Tax Amount (18%)</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Total Charges (incl. GST)</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Invoice</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Raw AWB</th>
                        </tr>
                      </thead>
                      <tbody>
                        {b2cData.length === 0 ? (
                          <tr><td colSpan={10} className="text-center p-8 text-muted-foreground">No B2C billing data available</td></tr>
                        ) : b2cData.map((row, i) => {
                          const dlKey = `dl-${row.seller_id}-${row.year}-${row.month}`;
                          const rawKey = `raw-b2c-${row.seller_id}-${row.year}-${row.month}`;
                          // B2C charges are GST-inclusive @18%, so taxable = total / 1.18.
                          const total = Number(row.total_charges) || 0;
                          const taxable = total / 1.18;
                          const taxAmount = total - taxable;
                          return (
                            <tr key={i} className="border-b hover:bg-muted/30">
                              <td className="px-4 py-3 align-middle font-medium">{MONTH_NAMES[row.month]} {row.year}</td>
                              <td className="px-4 py-3 align-middle">{row.seller_name}</td>
                              <td className="px-4 py-3 align-middle text-right">{row.forward_orders}</td>
                              <td className="px-4 py-3 align-middle text-right">{row.return_orders}</td>
                              <td className="px-4 py-3 align-middle text-right font-medium">{row.total_orders}</td>
                              <td className="px-4 py-3 align-middle text-right">₹{fmt(taxable)}</td>
                              <td className="px-4 py-3 align-middle text-right text-slate-500">₹{fmt(taxAmount)}</td>
                              <td className="px-4 py-3 align-middle text-right font-medium text-green-700">₹{fmt(row.total_charges)}</td>
                              <td className="px-4 py-3 align-middle text-center">
                                {row.invoice_number ? (
                                  <div className="flex flex-col items-center gap-1">
                                    <span className="text-xs font-medium text-green-700">{row.invoice_number}</span>
                                    <Button variant="ghost" size="sm" onClick={() => downloadB2CInvoice(row)} disabled={b2cGenerating === dlKey}
                                      className="text-green-600 hover:bg-green-50 h-6 px-2" data-testid={`b2c-download-invoice-${row.seller_id}-${row.year}-${row.month}`}>
                                      {b2cGenerating === dlKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400" title="Invoices are generated by the external billing system and written back via the API; they appear here once received.">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 align-middle text-center">
                                <Button variant="ghost" size="sm" onClick={() => downloadRawShipments(row, "b2c")}
                                  disabled={downloadingRaw === rawKey || !row.total_orders}
                                  title="Download per-shipment raw data (AWB + freight breakdown) for this seller & month"
                                  className="text-slate-600 hover:bg-slate-100 h-6 px-2" data-testid={`download-raw-b2c-${row.seller_id}-${row.year}-${row.month}`}>
                                  {downloadingRaw === rawKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {b2cTotalPages > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t">
                      <span className="text-sm text-muted-foreground">Page {b2cPage} of {b2cTotalPages}</span>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setB2cPage(p => Math.max(1, p - 1))} disabled={b2cPage === 1}><ChevronLeft className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm" onClick={() => setB2cPage(p => Math.min(b2cTotalPages, p + 1))} disabled={b2cPage === b2cTotalPages}><ChevronRight className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ======= SETTINGS TAB ======= */}
        <TabsContent value="settings">
          {settingsLoading ? (
            <div className="flex flex-col items-center justify-center h-[40vh]">
              <Loader2 className="w-12 h-12 animate-spin text-emerald-500" />
              <p className="text-slate-500 mt-4 text-lg">Loading Settings...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {settings.last_updated_at && (
                <div className="text-sm text-slate-500 bg-slate-50 px-4 py-2 rounded-lg inline-block">
                  Last updated: {new Date(settings.last_updated_at).toLocaleString("en-IN")} by {settings.last_updated_by || "—"}
                </div>
              )}

              {/* Bill From */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building className="w-5 h-5 text-emerald-600" />
                    Bill From (Service Provider)
                  </CardTitle>
                  <CardDescription>Your company billing information for invoices</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="ab-from-name">Company Registered Name *</Label>
                    <Input id="ab-from-name" value={settings.bill_from?.name || ""} onChange={(e) => updateBillFrom("name", e.target.value)} placeholder="Enter company name" className="mt-1" data-testid="admin-bill-from-name" />
                  </div>
                  <div>
                    <Label htmlFor="ab-from-address">Registered Address *</Label>
                    <Textarea id="ab-from-address" value={settings.bill_from?.address || ""} onChange={(e) => updateBillFrom("address", e.target.value)} placeholder="Enter full address" className="mt-1" rows={3} data-testid="admin-bill-from-address" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ab-from-gst">GST Number *</Label>
                      <Input id="ab-from-gst" value={settings.bill_from?.gst_number || ""} onChange={(e) => updateBillFrom("gst_number", e.target.value.toUpperCase())} placeholder="e.g., 27AABCU9603R1ZM" className="mt-1 uppercase" maxLength={15} data-testid="admin-bill-from-gst" />
                    </div>
                    <div>
                      <Label htmlFor="ab-from-state">State Code *</Label>
                      <Input id="ab-from-state" value={settings.bill_from?.state_code || ""} onChange={(e) => updateBillFrom("state_code", e.target.value)} placeholder="e.g., 27" className="mt-1" maxLength={2} data-testid="admin-bill-from-state" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bank Details */}
              <Card className="border-0 shadow-lg" data-testid="admin-bank-details-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Landmark className="w-5 h-5 text-teal-600" />
                    Bank Details (for Invoice)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ab-bank-account">Bank Account Number</Label>
                      <Input id="ab-bank-account" value={settings.bank_details?.account_number || ""} onChange={(e) => updateBankDetails("account_number", e.target.value)} placeholder="e.g., 195605000175" className="mt-1" data-testid="admin-bank-account" />
                    </div>
                    <div>
                      <Label htmlFor="ab-bank-name">Bank Name</Label>
                      <Input id="ab-bank-name" value={settings.bank_details?.bank_name || ""} onChange={(e) => updateBankDetails("bank_name", e.target.value.toUpperCase())} placeholder="e.g., ICICI BANK LIMITED" className="mt-1 uppercase" data-testid="admin-bank-name" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ab-bank-ifsc">IFSC Code</Label>
                      <Input id="ab-bank-ifsc" value={settings.bank_details?.ifsc_code || ""} onChange={(e) => updateBankDetails("ifsc_code", e.target.value.toUpperCase())} placeholder="e.g., ICIC0001956" className="mt-1 uppercase" maxLength={11} data-testid="admin-bank-ifsc" />
                    </div>
                    <div>
                      <Label htmlFor="ab-bank-city">City</Label>
                      <Input id="ab-bank-city" value={settings.bank_details?.city || ""} onChange={(e) => updateBankDetails("city", e.target.value.toUpperCase())} placeholder="e.g., MUMBAI" className="mt-1 uppercase" data-testid="admin-bank-city" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="ab-cheque">For Cheque, Payable To</Label>
                    <Input id="ab-cheque" value={settings.bank_details?.cheque_payable_to || ""} onChange={(e) => updateBankDetails("cheque_payable_to", e.target.value.toUpperCase())} placeholder="e.g., COMPANY NAME PRIVATE LIMITED" className="mt-1 uppercase" data-testid="admin-cheque-payable" />
                  </div>
                </CardContent>
              </Card>

              {/* GST State Codes Reference */}
              <Card className="border-0 shadow-lg">
                <CardContent className="pt-6">
                  <p className="text-sm text-slate-600 mb-2 font-medium">Common GST State Codes:</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {[
                      { code: "27", state: "Maharashtra" }, { code: "29", state: "Karnataka" },
                      { code: "36", state: "Telangana" }, { code: "33", state: "Tamil Nadu" },
                      { code: "07", state: "Delhi" }, { code: "06", state: "Haryana" },
                      { code: "09", state: "Uttar Pradesh" }, { code: "24", state: "Gujarat" },
                      { code: "19", state: "West Bengal" }, { code: "32", state: "Kerala" },
                    ].map(item => (
                      <span key={item.code} className="bg-slate-100 px-2 py-1 rounded">{item.code} - {item.state}</span>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Info: Bill To is per-seller */}
              <Card className="border-0 shadow-lg bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-blue-800">
                    <strong>Bill To</strong> is automatically populated from each seller's registered details (Company Name, GST, Address) when generating invoices.
                    GST type (CGST+SGST vs IGST) is determined by comparing the Bill From state code with the seller's GST state code.
                  </p>
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={handleSaveSettings} disabled={saving} style={{ backgroundColor: "#1B8C3D" }} size="lg" data-testid="save-admin-billing-settings">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {saving ? "Saving..." : "Save All Settings"}
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

    </div>
    </AdminLayout>
  );
};

export default AdminBilling;
